import { Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgStyle, NgTemplateOutlet } from "@angular/common";
import { StandingDataView } from "../standing-data-view";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { ToastrService } from "ngx-toastr";
import { FilterTree, ResetTreeFilter } from "@methods/TreeMethods";
import { firstValueFrom } from "rxjs";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";
import { CerrixIconViewerComponent } from "@app/shared/components/cerrix-icon-viewer/cerrix-icon-viewer.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/standingdata/i18n/scope";

@Component({
    selector: "standing-data-tree-view",
    templateUrl: "./standing-data-tree-view.component.html",
    styleUrls: ["./standing-data-tree-view.component.scss"],
    imports: [
        FormsModule,
        InputTooltipDirective,
        NgStyle,
        NgTemplateOutlet,
        CerrixIconViewerComponent,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)]
})
export class StandingDataTreeViewComponent extends StandingDataView {
    private _toastr = inject(ToastrService);
    private _translation = injectTranslation(scope);

    data: CerrixTreeItem[];
    dataLoading = false;

    sortLoading = false;
    sortMessage = "";

    currentlyDragging: CerrixTreeItem;
    currentlyDraggingParent: CerrixTreeItem;
    currentlyDraggingOver: CerrixTreeItem;
    dropAbove = false;
    dropBelow = false;

    searchFilter = "";

    loadData(selected?: number) {
        this.data = null;
        this.dataLoading = true;
        this.dataService.getMaintenanceListByType(this.standingDataType).subscribe((x) => {
            this.data = x as CerrixTreeItem[];
            this.selected = selected;
            this.dataLoading = false;

            this.onDataLoaded.emit(this.data);
        });
    }

    toggleEditMode() {
        if (!this.editMode) {
            this.editMode = true;
            this.resetSearchFilter();
        } else {
            this.editMode = false;
            this.changeCallback(this.selected);
            if (this.searchFilter && this.searchFilter.length > 0) {
                this.search();
            }
        }
    }

    addNew(parentID?: number) {
        if (!this.editMode) {
            let row = {};

            if (this.config.onAdd) {
                const parentNode = parentID > 0 ? this.currentRow : null;
                row = this.config.onAdd(parentNode);
            }

            if (parentID > 0) {
                row[this.config.parentProp] = parentID;
            }

            this.addClick(row);
        }
    }

    search() {
        this.data = FilterTree(this.data, this.searchFilter);
    }

    resetSearchFilter() {
        this.searchFilter = "";

        ResetTreeFilter(this.data);
    }

    dragStart(node: CerrixTreeItem, parentNode: CerrixTreeItem, ev) {
        node.isExpanded = false;
        node.show = false;
        this.currentlyDragging = node;
        this.currentlyDraggingParent = parentNode;
        ev.stopPropagation();
    }

    dragStop(node: CerrixTreeItem, ev) {
        if (this.currentlyDragging && node.ID === this.currentlyDragging.ID) {
            node.show = true;
            this.currentlyDragging = null;
            this.currentlyDraggingParent = null;
            this.dragEnter(null);
            this.dropBelow = this.dropBelow = false;
        }

        ev.stopPropagation();
    }

    async dragDrop(target: CerrixTreeItem, parentNode: CerrixTreeItem, ev) {
        if (
            this.sortLoading ||
            this.currentlyDragging == null ||
            target.ID === this.currentlyDragging.ID
        ) {
            return;
        }
        this.sortMessage = "";
        this.sortLoading = true;

        // Make copy of some variables, because DragDrop lifecycle will call dropStop eventually after this method
        // which causes these values to be reset.
        const dropAbove = this.dropAbove;
        const dropBelow = this.dropBelow;
        const movingObj = this.currentlyDragging;
        const movingParent = this.currentlyDraggingParent;
        await this._translation.ready();

        let targetIndex: number;
        let targetParent: CerrixTreeItem;
        let targetList: CerrixTreeItem[];
        if (dropAbove || dropBelow) {
            targetList = this.data;
            if (parentNode !== null) {
                targetList = parentNode.Children;
                parentNode.isExpanded = true;
            }

            targetIndex = targetList.indexOf(target) + (dropBelow ? 1 : 0);
            targetParent = parentNode;
        } else {
            if (!target.Children) {
                target.Children = [];
            }

            targetList = target.Children;
            targetIndex = target.Children.length;
            targetParent = target;
        }

        const parentChanged = Boolean(
            (!targetParent && movingObj.ParentID) ||
                (targetParent && targetParent.ID !== movingObj.ParentID)
        );

        // The API takes the index in the list that still holds the moved item. Locally the item is
        // removed first, so a move down within the same parent lands one place higher.
        const oldIndex = parentChanged ? -1 : targetList.indexOf(movingObj);
        const insertIndex = oldIndex >= 0 && targetIndex > oldIndex ? targetIndex - 1 : targetIndex;

        const messageParams: { name: string; position: number; parent?: string } = {
            name: movingObj.Name,
            position: insertIndex + 1,
        };
        if (parentChanged) {
            messageParams.parent = targetParent
                ? targetParent.Name
                : this._translation.t("treeView.rootParentName");
        }

        this.sortMessage = this._translation.t(
            parentChanged
                ? "treeView.movingMessageWithParent"
                : "treeView.movingMessage",
            messageParams
        );

        let updateResult: string;
        try {
            updateResult = await firstValueFrom(this.dataService
                .sortByType(
                    this.standingDataType,
                    movingObj.ID,
                    targetIndex,
                    targetParent ? targetParent.ID : null
                ));
        } catch {
            // The HTTP response interceptor already shows the error toast.
            this.sortLoading = false;
            return;
        }

        if (updateResult) {
            this._toastr.error(
                updateResult,
                this._translation.t(
                    parentChanged
                        ? "treeView.moveFailedTitleWithParent"
                        : "treeView.moveFailedTitle",
                    messageParams
                )
            );
            this.sortLoading = false;
            return;
        }

        this.removeFromParent(movingParent, movingObj);
        movingObj.ParentID = targetParent ? targetParent.ID : null;
        if (targetParent) {
            targetParent.isExpanded = true;
        }
        targetList.splice(insertIndex, 0, movingObj);

        this._toastr.success(
            this._translation.t(
                parentChanged
                    ? "treeView.moveSucceededMessageWithParent"
                    : "treeView.moveSucceededMessage",
                messageParams
            ),
            this._translation.t("treeView.successTitle")
        );
        this.animateMovedNode(movingObj);
        this.sortLoading = false;
    }

    dragEnter(node: CerrixTreeItem) {
        if (this.currentlyDraggingOver) {
            if (node && this.currentlyDraggingOver.ID === node.ID) {
                return;
            }

            this.currentlyDraggingOver.highlight = false;
            this.currentlyDraggingOver = null;
        }

        if (node) {
            node.highlight = true;
            this.currentlyDraggingOver = node;
        }
    }

    dragOver(ev) {
        const target = this.getNodeElem(ev.target);
        const sectionHeight = target.offsetHeight;
        const cursorLocation = ev.offsetY;
        const margin = 10;

        this.dropAbove = this.dropBelow = false;
        if (cursorLocation <= margin) {
            this.dropAbove = true;
        } else if (cursorLocation >= sectionHeight - margin) {
            this.dropBelow = true;
        }
    }

    getNodeElem(target) {
        let iteration = null;
        let stop = false;
        while (!stop) {
            iteration = iteration ? iteration.parentElement : target;
            if (!iteration || iteration.classList.contains("node")) {
                stop = true;
            }
        }

        return iteration;
    }

    removeFromParent(parent: CerrixTreeItem, node: CerrixTreeItem) {
        let listToCheck = this.data;
        // Parent is null so node was in root.
        if (parent !== null) {
            listToCheck = parent.Children;
        }

        const nodeIndex = listToCheck.indexOf(node);
        listToCheck.splice(nodeIndex, 1);
    }

    animateMovedNode(node: CerrixTreeItem) {
        node["newlocation"] = true;
        setTimeout(() => {
            node["newlocation"] = false;
        }, 1000);
    }
}
